const DESKTOP_MQ = '(min-width: 750px)';

class MasonryGallery extends HTMLElement {
  connectedCallback() {
    this.itemsWrap = this.querySelector('.masonry-gallery__items');
    this.dialog = this.querySelector('.masonry-gallery__dialog');
    this.dialogImage = this.querySelector('.masonry-gallery__lightbox-image');
    this.dialogVideo = this.querySelector('.masonry-gallery__lightbox-video');
    this.closeBtn = this.querySelector('[data-masonry-close]');
    this.prevBtn = this.querySelector('[data-masonry-prev]');
    this.nextBtn = this.querySelector('[data-masonry-next]');
    this.activeIndex = 0;

    this._onClick = this._onClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onDialogClick = this._onDialogClick.bind(this);
    this._onDialogClose = this._onDialogClose.bind(this);
    this._onResize = this._onResize.bind(this);

    this.layout();
    this.addEventListener('click', this._onClick);
    this.dialog?.addEventListener('click', this._onDialogClick);
    this.dialog?.addEventListener('keydown', this._onKeydown);
    this.dialog?.addEventListener('close', this._onDialogClose);
    window.addEventListener('resize', this._onResize);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this.dialog?.removeEventListener('click', this._onDialogClick);
    this.dialog?.removeEventListener('keydown', this._onKeydown);
    this.dialog?.removeEventListener('close', this._onDialogClose);
    window.removeEventListener('resize', this._onResize);
  }

  columnCount() {
    return window.matchMedia(DESKTOP_MQ).matches ? 3 : 2;
  }

  layout() {
    if (!this.itemsWrap) return;

    const items = [...this.querySelectorAll('.masonry-gallery__item')];
    const cols = this.columnCount();

    if (this._colCount === cols && this.classList.contains('is-masonry')) {
      return;
    }

    this._colCount = cols;

    const columns = Array.from({ length: cols }, () => {
      const col = document.createElement('div');
      col.className = 'masonry-gallery__col';
      return col;
    });

    items.forEach((item, index) => {
      columns[index % cols].appendChild(item);
    });

    this.itemsWrap.replaceChildren(...columns);
    this.classList.add('is-masonry');
  }

  _onResize() {
    this.layout();
  }

  triggers() {
    return [...this.querySelectorAll('.masonry-gallery__trigger')];
  }

  _onClick(event) {
    const close = event.target.closest('[data-masonry-close]');
    if (close) {
      this.dialog?.close();
      return;
    }

    if (event.target.closest('[data-masonry-prev]')) {
      this.show(this.activeIndex - 1);
      return;
    }

    if (event.target.closest('[data-masonry-next]')) {
      this.show(this.activeIndex + 1);
      return;
    }

    const trigger = event.target.closest('.masonry-gallery__trigger');
    if (!trigger || !this.dialog) return;

    const index = this.triggers().indexOf(trigger);
    if (index < 0) return;
    this.open(index);
  }

  _onDialogClick(event) {
    if (event.target === this.dialog) this.dialog.close();
  }

  _onDialogClose() {
    this.stopLightboxVideo();
  }

  _onKeydown(event) {
    if (event.key === 'ArrowLeft') this.show(this.activeIndex - 1);
    if (event.key === 'ArrowRight') this.show(this.activeIndex + 1);
  }

  stopLightboxVideo() {
    if (!this.dialogVideo) return;
    this.dialogVideo.pause();
    this.dialogVideo.removeAttribute('src');
    this.dialogVideo.load();
  }

  open(index) {
    this.show(index);
    if (!this.dialog.open) this.dialog.showModal();
  }

  show(index) {
    const items = this.triggers();
    if (!items.length) return;

    const next = (index + items.length) % items.length;
    this.activeIndex = next;
    const trigger = items[next];
    const src = trigger.getAttribute('data-full-src');
    const alt = trigger.getAttribute('data-alt') || '';
    const type = trigger.getAttribute('data-media-type') || 'image';

    if (type === 'video' && this.dialogVideo && src) {
      if (this.dialogImage) this.dialogImage.hidden = true;
      this.dialogVideo.hidden = false;
      this.dialogVideo.muted = true;
      this.dialogVideo.src = src;
      this.dialogVideo.play().catch(() => {});
    } else if (this.dialogImage && src) {
      this.stopLightboxVideo();
      this.dialogVideo?.toggleAttribute('hidden', true);
      this.dialogImage.hidden = false;
      this.dialogImage.src = src;
      this.dialogImage.alt = alt;
    }

    const multiple = items.length > 1;
    this.prevBtn?.toggleAttribute('hidden', !multiple);
    this.nextBtn?.toggleAttribute('hidden', !multiple);
  }
}

if (!customElements.get('masonry-gallery')) {
  customElements.define('masonry-gallery', MasonryGallery);
}
